import { motion } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { trpc } from "~/api/trpc";
import { GTMEvent } from "~/components/gtmProvider/GTMContext";
import useGTM from "~/components/gtmProvider/useGTM";
import { getDateLocaleByLocale, Language, useLanguageStore } from "~/stores/language-store";
import { Loader2, AlertCircle, Clock, CalendarDays, ArrowRight, Trash2, Mail } from "lucide-react";
import { GoCheckCircle } from "react-icons/go";
import TopProgressBar from "~/components/new/TopProgressBar";
import PrimaryActionButton from "~/components/new/PrimaryActionButton";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { toast } from "sonner";
import { Checkbox } from "~/components/ui/checkbox";
import { keepPreviousData } from "@tanstack/react-query";
import { de, enUS } from "date-fns/locale";
import { formatInTimeZone } from "date-fns-tz";
import LocalDateTime from "~/lib/localdatetime";
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { useScuid } from "~/hooks/use-scuid";

const WAITLIST_EMAIL_REGEX = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,}$/;
const phoneRegex = /^[\+]?[\d\s\(\)\-\.]{5,25}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type FlowStage = 'success' | 'withdrawn' | null;

const RadioIndicator = ({ isSelected }: { isSelected: boolean }) => {
    return (
        <div className={`w-5 h-5 rounded-full border-3 flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'border-burgundy' : 'border-burgundy/20'
            }`}>
            {isSelected && (
                <div className='w-5 h-5 rounded-full bg-burgundy' />
            )}
        </div>
    );
};

const WaitlistClaimYourSlot = () => {
    const { entryId } = useParams<{ entryId: string }>();
    const { t, language } = useLanguageStore();
    const { sendEvent } = useGTM();
    const utils = trpc.useUtils();
    const getScuid = useScuid();

    const [flowStage, setFlowStage] = useState<FlowStage>(null);
    const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
    const [rescheduleOrNewDialogOpen, setRescheduleOrNewDialogOpen] = useState(false);
    const [conflictingAppointmentId, setConflictingAppointmentId] = useState<string | null>(null);
    const [conflictingAppointmentStart, setConflictingAppointmentStart] = useState<{
        localDatetime: string;
        zone: string;
    } | null>(null);
    const [conflictingAppointmentStartUtcIso, setConflictingAppointmentStartUtcIso] = useState<string | null>(null);
    const [selectedTimeslot, setSelectedTimeslot] = useState<LocalDateTime | null>(null);
    const [bookedTimeslot, setBookedTimeslot] = useState<LocalDateTime | null>(null);
    const [agreeTermsConditionsAndPrivacyPolicy, setAgreeTermsConditionsAndPrivacyPolicy] = useState<boolean>(false);

    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [phone, setPhone] = useState<string>('');

    const slotViewedSentRef = useRef(false);
    const slotExpiredSentRef = useRef(false);

    const entryIdValid = !!entryId && UUID_RE.test(entryId);

    const timeslotsQuery = trpc.publicAppointments.getWaitlistAvailabilitiesV2.useQuery(
        {
            waitlistEntryId: entryId ?? '',
        },
        {
            enabled: entryIdValid,
            refetchInterval: (query) => {
                const d = query.state.data;
                const slots = d?.availableTimeslots ?? [];
                return d != null && slots.length === 0 ? 5000 : false;
            },
            refetchOnReconnect: false,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            placeholderData: keepPreviousData,
        }
    );

    const hasLoaded = timeslotsQuery.isFetched;
    const entryGone = hasLoaded && (timeslotsQuery.data === null || !entryIdValid);
    const slotPayload = timeslotsQuery.data;
    const timeslots = useMemo(
        () => slotPayload?.availableTimeslots ?? [],
        [slotPayload?.availableTimeslots]
    );
    const hasSlots = timeslots.length > 0;

    const withdrawMutation = trpc.publicAppointments.withdrawWaitlistSubmissionV2.useMutation({
        onSuccess: async () => {
            toast.success(t('waitlistWithdrawSuccess'));
            setWithdrawDialogOpen(false);
            await utils.publicAppointments.getWaitlistAvailabilitiesV2.invalidate({
                waitlistEntryId: entryId ?? '',
            });
            setFlowStage('withdrawn');
        },
        onError: () => {
            toast.error(t('waitlistWithdrawError'));
        },
    });

    const serviceDisplayname = useMemo(() => {
        return slotPayload?.title ?? null;
    }, [slotPayload?.title]);

    const waitlistEntry = useMemo(() => {
        return slotPayload?.entry ?? null;
    }, [slotPayload?.entry]);

    const onBookingSuccess = () => {
        setBookedTimeslot(selectedTimeslot);
        setFlowStage('success');
        sendEvent(GTMEvent.WAITLIST_SLOT_BOOKED, {
            waitlist_entry_id: entryId,
            service_name: serviceDisplayname,
        });
        toast.success(t('appointmentBookedSuccessfully'));
    };

    const bookAppointmentMutation = trpc.publicAppointments.bookAppointment.useMutation({
        onSuccess: onBookingSuccess,
        onError: (error) => {
            console.error('Booking error:', error);
            toast.error(t('appointmentBookingFailed'));
        },
    });

    const rescheduleAppointmentMutation = trpc.publicAppointments.rescheduleAppointment.useMutation({
        onSuccess: onBookingSuccess,
        onError: (error) => {
            console.error('Reschedule error:', error);
            toast.error(t('appointmentBookingFailed'));
        },
    });

    const checkDuplicateMutation = trpc.publicAppointments.checkWaitlistUpcomingDuplicateBooking.useMutation();

    const requestMagicLinkMutation = trpc.publicCustomer.requestBookingWidgetMagicLinkEmail.useMutation();
    const [loginLinkSent, setLoginLinkSent] = useState(false);

    useEffect(() => {
        if (!hasSlots || timeslots.length === 0) {
            setSelectedTimeslot(null);
            return;
        }
        const first = LocalDateTime.deserialize(timeslots[0]);
        setSelectedTimeslot((prev) => {
            if (!prev) {
                return first;
            }
            const stillListed = timeslots.some((s) => LocalDateTime.deserialize(s).equals(prev));
            return stillListed ? prev : first;
        });
    }, [hasSlots, timeslots]);

    useEffect(() => {
        if (waitlistEntry) {
            setFirstName(waitlistEntry.firstName ?? '');
            setLastName(waitlistEntry.lastName ?? '');
            setEmail(waitlistEntry.email);
            setPhone(waitlistEntry.phone ?? '');
        }
    }, [waitlistEntry]);

    useEffect(() => {
        if (!hasLoaded || entryGone || !hasSlots || !entryId) {
            return;
        }
        if (slotViewedSentRef.current) {
            return;
        }
        slotViewedSentRef.current = true;
        sendEvent(GTMEvent.WAITLIST_SLOT_VIEWED, {
            waitlist_entry_id: entryId,
            service_name: slotPayload?.title ?? null,
            available_timeslots_count: timeslots.length,
        });
    }, [hasLoaded, entryGone, hasSlots, entryId, sendEvent, slotPayload?.title, timeslots.length]);

    useEffect(() => {
        if (!hasLoaded || entryGone || hasSlots || !entryId || !slotPayload) {
            return;
        }
        if (slotExpiredSentRef.current) {
            return;
        }
        slotExpiredSentRef.current = true;
        sendEvent(GTMEvent.WAITLIST_SLOT_EXPIRED, {
            waitlist_entry_id: entryId,
            service_name: slotPayload.title ?? null,
        });
    }, [hasLoaded, entryGone, hasSlots, entryId, sendEvent, slotPayload]);

    const canSubmitBook = useMemo(() => {
        const fn = firstName.trim();
        const ln = lastName.trim();
        const em = email.trim();
        const ph = phone.trim();
        return (
            !!selectedTimeslot &&
            agreeTermsConditionsAndPrivacyPolicy &&
            fn.length >= 1 &&
            ln.length >= 1 &&
            WAITLIST_EMAIL_REGEX.test(em) &&
            phoneRegex.test(ph)
        );
    }, [firstName, lastName, email, phone, selectedTimeslot, agreeTermsConditionsAndPrivacyPolicy]);

    const executeNewBookingFromWaitlist = async () => {
        if (!waitlistEntry || !selectedTimeslot || !entryId) {
            return;
        }
        await bookAppointmentMutation.mutateAsync({
            storeId: waitlistEntry.store?.id ?? -1,
            selectedStartTime: selectedTimeslot.toJson(),
            bookableItems: waitlistEntry.bookableItems,
            email: email.trim().toLowerCase(),
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            phone: phone.trim(),
            cameFromWaitlist: true,
            waitlistSubmissionV2Id: entryId,
            scuid: await getScuid(),
        });
    };

    const handleBookAppointment = async () => {
        if (!waitlistEntry || !selectedTimeslot || !canSubmitBook || !entryId) {
            return;
        }

        try {
            if (slotPayload?.isLoggedIn) {
                const check = await checkDuplicateMutation.mutateAsync({
                    waitlistEntryId: entryId,
                    email: email.trim().toLowerCase(),
                });
                if (check.conflictingAppointmentId) {
                    setConflictingAppointmentId(check.conflictingAppointmentId);
                    setConflictingAppointmentStart(check.conflictingAppointmentStart ?? null);
                    setConflictingAppointmentStartUtcIso(check.conflictingAppointmentStartUtcIso ?? null);
                    setRescheduleOrNewDialogOpen(true);
                    return;
                }
            }
            await executeNewBookingFromWaitlist();
        } catch (e) {
            console.error(e);
            toast.error(t('appointmentBookingFailed'));
        }
    };

    const handleConfirmRescheduleFromDialog = async () => {
        if (!conflictingAppointmentId || !selectedTimeslot || !entryId) {
            return;
        }
        try {
            await rescheduleAppointmentMutation.mutateAsync({
                appointmentId: conflictingAppointmentId,
                selectedStartTime: selectedTimeslot.toJson(),
                waitlistSubmissionV2Id: entryId,
                confirmEmail: email.trim().toLowerCase(),
            });
            setRescheduleOrNewDialogOpen(false);
            setConflictingAppointmentId(null);
            setConflictingAppointmentStart(null);
            setConflictingAppointmentStartUtcIso(null);
        } catch {
            // toast in onError
        }
    };

    const handleConfirmBookNewFromDialog = async () => {
        setRescheduleOrNewDialogOpen(false);
        setConflictingAppointmentId(null);
        setConflictingAppointmentStart(null);
        setConflictingAppointmentStartUtcIso(null);
        try {
            await executeNewBookingFromWaitlist();
        } catch {
            // toast in onError
        }
    };

    const handleRetry = () => {
        timeslotsQuery.refetch();
    };

    const handleConfirmWithdraw = () => {
        if (!entryId || !email.trim()) {
            return;
        }
        withdrawMutation.mutate({
            waitlistEntryId: entryId,
            email: email.trim().toLowerCase(),
        });
    };

    const conflictingAppointmentStartDisplay = useMemo(() => {
        const dfLocale = language === Language.GERMAN ? de : enUS;
        if (conflictingAppointmentStartUtcIso) {
            const d = new Date(conflictingAppointmentStartUtcIso);
            if (Number.isNaN(d.getTime())) {
                return null;
            }
            const zone =
                conflictingAppointmentStart?.zone?.replace(/_/g, '/') || 'Europe/Berlin';
            return {
                dateLine: formatInTimeZone(d, zone, 'EEEE, dd.MM.yyyy', { locale: dfLocale }),
                timeLine:
                    formatInTimeZone(d, zone, 'HH:mm', { locale: dfLocale }) +
                    (language === Language.GERMAN ? ' Uhr' : ''),
            };
        }
        if (!conflictingAppointmentStart) {
            return null;
        }
        try {
            const ldt = LocalDateTime.deserialize(conflictingAppointmentStart);
            return {
                dateLine: ldt.format('EEEE, dd.MM.yyyy', { locale: getDateLocaleByLocale(language) }),
                timeLine: `${ldt.format('HH:mm', { locale: getDateLocaleByLocale(language) })}${language === Language.GERMAN ? ' Uhr' : ''}`,
            };
        } catch {
            return null;
        }
    }, [conflictingAppointmentStart, conflictingAppointmentStartUtcIso, language]);

    const bookingOrCheckInFlight =
        bookAppointmentMutation.isPending ||
        rescheduleAppointmentMutation.isPending ||
        checkDuplicateMutation.isPending;

    const withdrawDisabled =
        !waitlistEntry ||
        bookingOrCheckInFlight ||
        withdrawMutation.isPending;

    // Success
    if (flowStage === 'success' && bookedTimeslot) {
        return (
            <motion.div
                className='flex flex-col min-h-screen'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
            >
                <TopProgressBar percentageFilled={100} displayString="BESTÄTIGT" color="green" />
                <div className='flex flex-col gap-6 px-4 pt-8 pb-16'>
                    <div className="relative w-full p-4 rounded-[5px] flex flex-col gap-8 drop-shadow-md">
                        <div className='flex flex-col items-center gap-4 text-center'>
                            <GoCheckCircle className='w-10 h-10 text-green-600' />
                            <div className='flex flex-col gap-3'>
                                <span className='text-xl text-burgundy font-normal'>
                                    {t('appointmentConfirmed')}
                                </span>
                                <span className='text-sm text-burgundy/70 font-light'>
                                    {t('yourAppointmentHasBeenSuccessfullyBooked')}
                                </span>
                            </div>
                        </div>

                        <div className='rounded-[5px] bg-white border-2 border-burgundy/20 p-4 drop-shadow-md'>
                            <div className='flex flex-col gap-3'>
                                <div className='flex items-center gap-3 text-burgundy'>
                                    <CalendarDays className='h-4 w-4 shrink-0' />
                                    <span className='text-sm font-light'>
                                        {bookedTimeslot.format("EEEE, dd.MM.yyyy", { locale: getDateLocaleByLocale(language) })}
                                    </span>
                                </div>
                                <div className='h-px bg-burgundy/50 w-full' />
                                <div className='flex items-center gap-3 text-burgundy'>
                                    <Clock className='w-4 h-4 shrink-0' />
                                    <span className='text-sm font-light'>
                                        {bookedTimeslot.format("HH:mm", { locale: getDateLocaleByLocale(language) })} Uhr
                                    </span>
                                </div>
                            </div>
                        </div>

                        {serviceDisplayname && (
                            <div className='rounded-[5px] bg-white border-2 border-burgundy/20 p-4 drop-shadow-md'>
                                <p className='text-sm text-burgundy font-light'>
                                    <span className='font-normal'>{t('service')}:</span> {serviceDisplayname}
                                </p>
                            </div>
                        )}

                        <p className='text-xs text-burgundy/70 text-center font-light'>
                            {t('confirmationEmailSentTo')} {waitlistEntry?.email}
                        </p>
                    </div>
                </div>
            </motion.div>
        );
    }

    // Withdrawn
    if (flowStage === 'withdrawn') {
        return (
            <motion.div
                className='flex flex-col min-h-screen'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
            >
                <TopProgressBar percentageFilled={50} displayString="WAITLIST" />
                <div className='flex flex-col items-center justify-center flex-1 px-6'>
                    <div className='flex flex-col items-center gap-6 max-w-md mx-auto text-center'>
                        <GoCheckCircle className='w-10 h-10 text-burgundy/80' />
                        <div className='flex flex-col gap-2'>
                            <h1 className='text-xl text-burgundy font-normal'>{t('waitlistWithdrawnTitle')}</h1>
                            <p className='text-sm text-burgundy/70 font-light'>{t('waitlistWithdrawnDescription')}</p>
                        </div>
                    </div>
                </div>
            </motion.div>
        );
    }

    // Loading
    if (!hasLoaded || timeslotsQuery.isLoading) {
        return (
            <motion.div
                className='flex flex-col min-h-screen'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
            >
                <TopProgressBar percentageFilled={50} displayString="WAITLIST" />
                <div className='flex flex-col items-center justify-center flex-1 px-6'>
                    <div className='flex flex-col items-center gap-6 max-w-md mx-auto text-center'>
                        <Loader2 className='h-8 w-8 animate-spin text-burgundy' />
                        <div className='flex flex-col gap-2'>
                            <h1 className='text-xl text-burgundy font-normal'>{t('loadingYourTimeslot')}</h1>
                            <p className='text-sm text-burgundy/70 font-light'>{t('pleaseWaitWhileWeLoadYourAvailableTimeslots')}</p>
                        </div>
                    </div>
                </div>
            </motion.div>
        );
    }

    // Login required for existing customer
    if (slotPayload && 'requiresLogin' in slotPayload && slotPayload.requiresLogin === true) {
        const handleSendLoginLink = async () => {
            try {
                await requestMagicLinkMutation.mutateAsync({
                    email: slotPayload.email,
                    next: `/waitlist-claim-your-slot/${entryId}`,
                });
                setLoginLinkSent(true);
            } catch {
                toast.error(t('waitlistLoginLinkError'));
            }
        };

        return (
            <motion.div
                className='flex flex-col min-h-screen'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
            >
                <TopProgressBar percentageFilled={50} displayString="WAITLIST" />
                <div className='flex flex-col items-center justify-center flex-1 px-6'>
                    <div className='flex flex-col items-center gap-6 max-w-md mx-auto text-center'>
                        <Mail className='h-12 w-12 text-burgundy' />
                        <div className='flex flex-col gap-2'>
                            <h1 className='text-xl text-burgundy font-normal'>{t('waitlistLoginRequired')}</h1>
                            <p className='text-sm text-burgundy/70 font-light'>{t('waitlistLoginDescription')}</p>
                        </div>
                        {loginLinkSent ? (
                            <div className='flex flex-col items-center gap-2'>
                                <GoCheckCircle className='w-8 h-8 text-green-600' />
                                <p className='text-sm text-burgundy/70 font-light'>{t('waitlistLoginLinkSent')}</p>
                            </div>
                        ) : (
                            <PrimaryActionButton
                                onClick={handleSendLoginLink}
                                label={t('waitlistSendLoginLink') as string}
                                disabled={requestMagicLinkMutation.isPending}
                                loading={requestMagicLinkMutation.isPending}
                            />
                        )}
                    </div>
                </div>
            </motion.div>
        );
    }

    // Entry missing, invalid id, or soft-deleted
    if (entryGone) {
        return (
            <motion.div
                className='flex flex-col min-h-screen'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
            >
                <TopProgressBar percentageFilled={50} displayString="WAITLIST" />
                <div className='flex flex-col items-center justify-center flex-1 px-6'>
                    <div className='flex flex-col items-center gap-6 max-w-md mx-auto text-center'>
                        <AlertCircle className='h-12 w-12 text-red-400' />
                        <div className='flex flex-col gap-2'>
                            <h1 className='text-xl text-burgundy font-normal'>{t('waitlistEntryNotAvailable')}</h1>
                        </div>
                    </div>
                </div>
            </motion.div>
        );
    }

    // No slots (retry)
    if (!hasSlots) {
        return (
            <motion.div
                className='flex flex-col min-h-screen'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
            >
                <TopProgressBar percentageFilled={50} displayString="WAITLIST" />
                <div className='flex flex-col items-center justify-center flex-1 px-6'>
                    <div className='flex flex-col items-center gap-6 max-w-md mx-auto text-center'>
                        <AlertCircle className='h-12 w-12 text-red-400' />
                        <div className='flex flex-col gap-2'>
                            <h1 className='text-xl text-burgundy font-normal'>{t('timeslotNoLongerAvailable')}</h1>
                            <p className='text-sm text-burgundy/70 font-light'>
                                {t('timeslotAlreadyTakenByOtherCustomer')}
                            </p>
                        </div>
                        <PrimaryActionButton
                            onClick={handleRetry}
                            label={t('checkForNewTimeslots')}
                            loading={timeslotsQuery.isFetching}
                        />
                    </div>
                </div>
            </motion.div>
        );
    }

    // Selection (has slots)
    return (
        <motion.div
            className='flex flex-col min-h-screen'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
        >
            <TopProgressBar percentageFilled={50} displayString="WAITLIST" />

            <div className='flex-1 overflow-y-auto px-4 pt-8 pb-8'>
                <div className='flex flex-col gap-6 max-w-md mx-auto w-full'>
                    <motion.div
                        className='flex flex-col items-center gap-2 text-center'
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.1, ease: "easeOut" }}
                    >
                        <h1 className='text-2xl text-burgundy text-center'>{t('claimYourTimeslot')}</h1>
                        <p className='text-sm text-burgundy/70 font-light'>{t('greatNewsTimeslotAvailable')}</p>
                    </motion.div>

                    {serviceDisplayname && (
                        <motion.div
                            className='rounded-[5px] bg-white border-2 border-burgundy/20 p-4 drop-shadow-md'
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: 0.15, ease: "easeOut" }}
                        >
                            {waitlistEntry && (
                                <p className='text-sm text-burgundy mb-3'>
                                    <span className='font-normal'>{t('clinic')}:</span>{' '}
                                    <span className='font-light'>{waitlistEntry?.store?.name ?? 'Digital'}</span>
                                </p>
                            )}
                            <div className='h-px bg-burgundy/50 w-full mb-3' />
                            <div className='flex flex-col gap-2'>
                                {waitlistEntry?.variants.map((variant, index) => (
                                    <div key={index} className='flex justify-between items-center text-sm text-burgundy'>
                                        <span className='font-light'>{variant.name}</span>
                                        <span className='font-light'>
                                            ab {new Intl.NumberFormat(language === Language.GERMAN ? 'de-DE' : 'en-GB', {
                                                style: 'currency',
                                                currency: 'EUR'
                                            }).format(variant.priceCents / 100)}
                                        </span>
                                    </div>
                                ))}
                                {waitlistEntry && waitlistEntry.variants.length > 1 && (
                                    <>
                                        <div className='h-px bg-burgundy/50 w-full mt-1' />
                                        <div className='flex justify-between items-center text-sm text-burgundy'>
                                            <span className='font-normal'>{t('total')}:</span>
                                            <span className='font-normal'>
                                                ab {new Intl.NumberFormat(language === Language.GERMAN ? 'de-DE' : 'en-GB', {
                                                    style: 'currency',
                                                    currency: 'EUR'
                                                }).format(
                                                    waitlistEntry.variants.reduce((sum, variant) =>
                                                        sum + variant.priceCents, 0
                                                    ) / 100
                                                )}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {waitlistEntry && (
                        <motion.div
                            className='rounded-[5px] bg-white border-2 border-burgundy/20 p-4 drop-shadow-md'
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: 0.2, ease: "easeOut" }}
                        >
                            <div className='flex flex-col gap-3'>
                                <Label className='text-sm font-normal text-burgundy'>{t('personalDetails')}</Label>
                                <p className='text-xs text-burgundy/70 font-light'>{t('waitlistClaimAllContactFieldsRequired')}</p>

                                <div className='flex gap-2'>
                                    <div className='flex-1 flex flex-col gap-1'>
                                        <Label htmlFor="wl-first" className='text-xs font-light text-burgundy'>
                                            {t('firstNameLabel')} <span className='text-burgundy/80'>{t('fieldRequiredShort')}</span>
                                        </Label>
                                        <Input
                                            id="wl-first"
                                            type="text"
                                            placeholder={t('enterFirstNamePlaceholder')}
                                            value={firstName}
                                            onChange={(e) => setFirstName(e.target.value)}
                                            className="shadow-none h-10 text-sm border-burgundy/20 text-burgundy placeholder:text-burgundy/50 focus-visible:ring-burgundy/50 rounded-[5px]"
                                        />
                                    </div>
                                    <div className='flex-1 flex flex-col gap-1'>
                                        <Label htmlFor="wl-last" className='text-xs font-light text-burgundy'>
                                            {t('lastNameLabel')} <span className='text-burgundy/80'>{t('fieldRequiredShort')}</span>
                                        </Label>
                                        <Input
                                            id="wl-last"
                                            type="text"
                                            placeholder={t('enterLastNamePlaceholder')}
                                            value={lastName}
                                            onChange={(e) => setLastName(e.target.value)}
                                            className="shadow-none h-10 text-sm border-burgundy/20 text-burgundy placeholder:text-burgundy/50 focus-visible:ring-burgundy/50 rounded-[5px]"
                                        />
                                    </div>
                                </div>

                                <div className='flex flex-col gap-1'>
                                    <Label htmlFor="wl-email" className='text-xs font-light text-burgundy'>
                                        {t('emailAddressLabel')} <span className='text-burgundy/80'>{t('fieldRequiredShort')}</span>
                                    </Label>
                                    <Input
                                        id="wl-email"
                                        type="email"
                                        placeholder={t('enterEmailAddressPlaceholder')}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="shadow-none h-10 text-sm border-burgundy/20 text-burgundy placeholder:text-burgundy/50 focus-visible:ring-burgundy/50 rounded-[5px]"
                                    />
                                </div>

                                <div className='flex flex-col gap-1'>
                                    <Label htmlFor="wl-phone" className='text-xs font-light text-burgundy'>
                                        {t('phoneNumberLabel')} <span className='text-burgundy/80'>{t('fieldRequiredShort')}</span>
                                    </Label>
                                    <Input
                                        id="wl-phone"
                                        type="tel"
                                        placeholder={t('enterPhoneNumberPlaceholder')}
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        className="shadow-none h-10 text-sm border-burgundy/20 text-burgundy placeholder:text-burgundy/50 focus-visible:ring-burgundy/50 rounded-[5px]"
                                    />
                                </div>
                            </div>
                        </motion.div>
                    )}

                    <AlertDialog
                        open={rescheduleOrNewDialogOpen}
                        onOpenChange={(open) => {
                            setRescheduleOrNewDialogOpen(open);
                            if (!open) {
                                setConflictingAppointmentId(null);
                                setConflictingAppointmentStart(null);
                                setConflictingAppointmentStartUtcIso(null);
                            }
                        }}
                    >
                        <AlertDialogContent className="border-burgundy/20">
                            <AlertDialogHeader>
                                <AlertDialogTitle className="text-burgundy text-left space-y-3">
                                    <span className="block text-xl font-normal leading-snug">
                                        {t('waitlistDuplicateAppointmentTitle')}
                                    </span>
                                    {conflictingAppointmentStartDisplay && (
                                        <div className="rounded-[5px] border border-burgundy/25 bg-burgundy/[0.06] px-3 py-3 text-left">
                                            <span className="block text-xs font-normal uppercase tracking-wide text-burgundy/75">
                                                {t('waitlistDuplicateAppointmentWouldMoveLabel')}
                                            </span>
                                            <span className="mt-2 block text-base font-light text-burgundy">
                                                {conflictingAppointmentStartDisplay.dateLine}
                                            </span>
                                            <span className="block text-base font-light text-burgundy">
                                                {conflictingAppointmentStartDisplay.timeLine}
                                            </span>
                                        </div>
                                    )}
                                    {selectedTimeslot && (
                                        <div className="rounded-[5px] border border-burgundy/25 bg-burgundy/[0.06] px-3 py-3 text-left">
                                            <span className="block text-xs font-normal uppercase tracking-wide text-burgundy/75">
                                                {t('waitlistDuplicateAppointmentNewTimeLabel')}
                                            </span>
                                            <span className="mt-2 block text-base font-light text-burgundy">
                                                {selectedTimeslot.format('EEEE, dd.MM.yyyy', { locale: getDateLocaleByLocale(language) })}
                                            </span>
                                            <span className="block text-base font-light text-burgundy">
                                                {selectedTimeslot.format('HH:mm', { locale: getDateLocaleByLocale(language) })}{language === Language.GERMAN ? ' Uhr' : ''}
                                            </span>
                                        </div>
                                    )}
                                </AlertDialogTitle>
                                <AlertDialogDescription className="text-burgundy/70 text-sm font-light text-left">
                                    {t('waitlistDuplicateAppointmentDescription')}
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
                                <Button
                                    type="button"
                                    className="w-full bg-burgundy text-white hover:bg-burgundy/90"
                                    disabled={
                                        bookAppointmentMutation.isPending ||
                                        rescheduleAppointmentMutation.isPending
                                    }
                                    onClick={handleConfirmRescheduleFromDialog}
                                >
                                    {rescheduleAppointmentMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                    ) : (
                                        t('waitlistRescheduleExistingButton')
                                    )}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full border-burgundy/20 text-burgundy"
                                    disabled={
                                        bookAppointmentMutation.isPending ||
                                        rescheduleAppointmentMutation.isPending
                                    }
                                    onClick={handleConfirmBookNewFromDialog}
                                >
                                    {bookAppointmentMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                    ) : (
                                        t('waitlistBookNewAppointmentButton')
                                    )}
                                </Button>
                                <AlertDialogCancel className="w-full border-burgundy/20 text-burgundy mt-0">
                                    {t('waitlistWithdrawCancelButton')}
                                </AlertDialogCancel>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>

                    <AlertDialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
                        <AlertDialogTrigger asChild>
                            <button
                                type="button"
                                disabled={withdrawDisabled}
                                className='flex items-center justify-center gap-2 w-full py-3 text-sm text-red-600 font-light border border-red-200 rounded-[5px] hover:bg-red-50 disabled:opacity-40 disabled:pointer-events-none transition-colors'
                            >
                                <Trash2 className='h-4 w-4 shrink-0 text-red-600' aria-hidden />
                                {t('waitlistWithdrawNotificationsLabel')}
                            </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="border-burgundy/20">
                            <AlertDialogHeader>
                                <AlertDialogTitle className="text-burgundy">{t('waitlistWithdrawConfirmTitle')}</AlertDialogTitle>
                                <AlertDialogDescription className="text-burgundy/70">
                                    {t('waitlistWithdrawConfirmDescription')}
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter className="gap-2 sm:gap-0">
                                <AlertDialogCancel className="border-burgundy/20 text-burgundy">
                                    {t('waitlistWithdrawCancelButton')}
                                </AlertDialogCancel>
                                <Button
                                    type="button"
                                    className="bg-red-600 text-white hover:bg-red-700"
                                    disabled={withdrawMutation.isPending || !WAITLIST_EMAIL_REGEX.test(email.trim())}
                                    onClick={handleConfirmWithdraw}
                                >
                                    {withdrawMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                    ) : (
                                        t('waitlistWithdrawConfirmButton')
                                    )}
                                </Button>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>

                    <motion.div
                        className='flex flex-col gap-3'
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.25, ease: "easeOut" }}
                    >
                        <Label className='text-lg font-normal text-burgundy'>{t('availableTimeslots')}</Label>

                        <div className='flex flex-col gap-3 max-h-[400px] overflow-y-auto'>
                            {timeslots.map((timeslotSerialized, index) => {
                                const timeslot = LocalDateTime.deserialize(timeslotSerialized);
                                const isSelected = selectedTimeslot !== null && timeslot.equals(selectedTimeslot);

                                return (
                                    <div
                                        key={`${timeslot.toUTC().getTime()}-${index}`}
                                        onClick={() => setSelectedTimeslot(timeslot)}
                                        className={`w-full flex justify-between items-center p-4 rounded-[5px] bg-white border-2 transition-all duration-200 cursor-pointer drop-shadow-md ${isSelected
                                            ? 'border-burgundy'
                                            : 'border-burgundy/20'
                                            }`}
                                    >
                                        <div className='flex flex-col items-start gap-1'>
                                            <span className='font-normal text-burgundy'>
                                                {timeslot.format("EEEE, dd.MM.yyyy", { locale: getDateLocaleByLocale(language) })}
                                            </span>
                                            <span className='text-sm text-burgundy/70 font-light'>
                                                {timeslot.format("HH:mm", { locale: getDateLocaleByLocale(language) })} {t('oClock')}
                                            </span>
                                        </div>
                                        <RadioIndicator isSelected={isSelected} />
                                    </div>
                                );
                            })}
                        </div>
                    </motion.div>

                    <motion.div
                        className='w-full flex flex-col gap-3'
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.3, ease: "easeOut" }}
                    >
                        <div className="flex items-center gap-4">
                            <Checkbox
                                id="terms"
                                checked={agreeTermsConditionsAndPrivacyPolicy}
                                onCheckedChange={(checked) => setAgreeTermsConditionsAndPrivacyPolicy(typeof checked === 'boolean' ? checked : false)}
                                className="shadow-none data-[state=checked]:bg-burgundy data-[state=checked]:border-burgundy border-burgundy/50"
                            />
                            <Label htmlFor="terms" className="text-sm text-burgundy font-light leading-relaxed">
                                {t('iAcceptTermsAndConditions').split(/(\{[^}]+\})/).map((part, index) => {
                                    const match = part.match(/\{([^|]+)\|([^}]+)\}/);
                                    if (match) {
                                        const [, text, link] = match;
                                        const href = link === 'AGB'
                                            ? 'https://kalialab.de/agb'
                                            : 'https://kalialab.de/datenschutzerklaerung/';
                                        return (
                                            <a
                                                key={index}
                                                href={href}
                                                className="text-primary-400 hover:text-primary-700"
                                                target='_blank'
                                            >
                                                {text}
                                            </a>
                                        );
                                    }
                                    return part;
                                })}
                            </Label>
                        </div>
                    </motion.div>
                </div>
            </div>

            <div className='px-6 pb-6'>
                <PrimaryActionButton
                    onClick={handleBookAppointment}
                    label={t('bookAppointment').toUpperCase()}
                    disabled={!canSubmitBook || bookingOrCheckInFlight}
                    loading={bookingOrCheckInFlight}
                    rightIcon={<ArrowRight />}
                />
                <p className='text-xs text-burgundy/70 text-center font-light mt-3'>
                    {t('byBookingYouConfirmAvailability')}
                </p>
            </div>
        </motion.div>
    );
};

export default WaitlistClaimYourSlot;
